import { Middleware } from '@reduxjs/toolkit';
import packageJson from '../../../package.json';

/**
 * Lightweight "flight recorder" for debugging issues that only reproduce on
 * real devices (typically mobile). A ring buffer keeps the most recent Redux
 * actions in memory; on error we snapshot that buffer together with the current
 * state and device context and ship it to /api/error.
 */

const MAX_ACTIONS = 40;
// Cap per-action payload + per-snapshot sizes so we never post megabytes from a
// flaky mobile connection. /api/error rejects large bodies with a 413.
const MAX_PAYLOAD_CHARS = 500;
const MAX_CONTEXT_CHARS = 60000;

export interface RecordedAction {
  type: string;
  t: number; // ms since the buffer started (relative timestamps keep it small)
  payload?: any;
}

export interface DebugSnapshot {
  version: string;
  url: string;
  userAgent: string;
  capturedAt: string;
  actions: RecordedAction[];
  state: unknown;
  video?: unknown;
  extra?: Record<string, unknown>;
}

// Human-readable labels for the numeric HTMLMediaElement enums, so a report can
// be read without cross-referencing the spec. Index === the numeric value.
const READY_STATE_LABELS = [
  'HAVE_NOTHING', // 0 — no data
  'HAVE_METADATA', // 1 — duration/dimensions known, no frame (dash wedges here)
  'HAVE_CURRENT_DATA', // 2 — current frame only
  'HAVE_FUTURE_DATA', // 3 — playable forward from here
  'HAVE_ENOUGH_DATA', // 4 — can play through
];
const NETWORK_STATE_LABELS = ['NETWORK_EMPTY', 'NETWORK_IDLE', 'NETWORK_LOADING', 'NETWORK_NO_SOURCE'];
const MEDIA_ERROR_LABELS: Record<number, string> = {
  1: 'MEDIA_ERR_ABORTED',
  2: 'MEDIA_ERR_NETWORK',
  3: 'MEDIA_ERR_DECODE',
  4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
};

function serializeTimeRanges(ranges: TimeRanges | undefined): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  if (!ranges) return out;
  for (let i = 0; i < ranges.length; i += 1) {
    out.push([+ranges.start(i).toFixed(2), +ranges.end(i).toFixed(2)]);
  }
  return out;
}

/**
 * Snapshot the live shared `#videoEl` DOM element. The Redux `player.state.video`
 * slice records our *intent* (assetId, play state, ready flag) but not what the
 * element itself is doing — and stuck-video bugs live in that gap: e.g. dash
 * wedging with readyState parked at 1 and no `canplay`, which looks fine in Redux
 * (ready:false) but is only diagnosable from the element's readyState /
 * networkState / buffered ranges / error. Captured on every report so those are
 * present next time instead of having to be inferred.
 */
export function collectVideoDiagnostics(): Record<string, unknown> | undefined {
  if (typeof document === 'undefined') return undefined;
  const el = document.getElementById('videoEl') as HTMLVideoElement | null;
  if (!el) return { present: false };
  try {
    const round = (n: number) => (Number.isFinite(n) ? +n.toFixed(2) : n);
    return {
      present: true,
      currentSrc: el.currentSrc || el.src || null,
      readyState: el.readyState,
      readyStateLabel: READY_STATE_LABELS[el.readyState] ?? String(el.readyState),
      networkState: el.networkState,
      networkStateLabel: NETWORK_STATE_LABELS[el.networkState] ?? String(el.networkState),
      paused: el.paused,
      seeking: el.seeking,
      ended: el.ended,
      muted: el.muted,
      volume: round(el.volume),
      currentTime: round(el.currentTime),
      duration: round(el.duration),
      buffered: serializeTimeRanges(el.buffered),
      seekable: serializeTimeRanges(el.seekable),
      error: el.error
        ? { code: el.error.code, label: MEDIA_ERROR_LABELS[el.error.code] ?? 'UNKNOWN', message: el.error.message }
        : null,
    };
  } catch {
    return { present: true, error: '<<video diagnostics failed>>' };
  }
}

let buffer: RecordedAction[] = [];
const startedAt = Date.now();

function truncate(value: unknown): unknown {
  if (value == null) return value;
  try {
    const json = JSON.stringify(value);
    if (json.length <= MAX_PAYLOAD_CHARS) return value;
    return `<<truncated ${json.length} chars>>`;
  } catch {
    return '<<unserializable>>';
  }
}

export const actionHistoryMiddleware: Middleware = () => next => (action: any) => {
  if (action && typeof action.type === 'string') {
    buffer.push({ type: action.type, t: Date.now() - startedAt, payload: truncate(action.payload) });
    if (buffer.length > MAX_ACTIONS) buffer.shift();
  }
  return next(action);
};

export function getActionHistory(): RecordedAction[] {
  return buffer.slice();
}

export function clearActionHistory(): void {
  buffer = [];
}

/**
 * Only the slices that are useful for reproducing a bug and small enough to
 * post. `entity` (the normalised entity cache) is deliberately excluded — it is
 * the bulk of the store and blows past the /api/error body limit.
 */
function pickState(state: any): unknown {
  if (!state) return state;
  return {
    state: state.state, // currently-selected entity ids
    player: state.core?.player,
    queue: state.queue,
    register: state.register,
    session: state.session,
  };
}

/**
 * Build a serialisable snapshot of recent actions + current state + device
 * context. Pass the store's getState (kept loose to avoid an import cycle).
 */
export function buildDebugSnapshot(getState: () => unknown, extra?: Record<string, unknown>): DebugSnapshot {
  return {
    version: packageJson.version,
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    capturedAt: new Date().toISOString(),
    actions: getActionHistory(),
    state: pickState(getState()),
    video: collectVideoDiagnostics(),
    ...(extra ? { extra } : {}),
  };
}

/**
 * Serialise a snapshot for the `context` field of /api/error, shedding detail
 * until it fits under MAX_CONTEXT_CHARS so the request can never 413.
 */
export function serializeDebugSnapshot(getState: () => unknown, extra?: Record<string, unknown>): string | undefined {
  const snapshot = buildDebugSnapshot(getState, extra);
  try {
    let json = JSON.stringify(snapshot);
    if (json.length <= MAX_CONTEXT_CHARS) return json;

    // 1. Drop action payloads, keeping the action sequence.
    json = JSON.stringify({ ...snapshot, actions: snapshot.actions.map(({ type, t }) => ({ type, t })) });
    if (json.length <= MAX_CONTEXT_CHARS) return json;

    // 2. Drop the state, keeping actions + device context.
    json = JSON.stringify({ ...snapshot, state: '<<omitted — too large>>' });
    if (json.length <= MAX_CONTEXT_CHARS) return json;

    // 3. Last resort: device context only.
    return JSON.stringify({ ...snapshot, actions: [], state: '<<omitted — too large>>' }).slice(0, MAX_CONTEXT_CHARS);
  } catch {
    return undefined;
  }
}
